import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useClerkGate, useStableGetToken } from '@/hooks/useClerkGate';
import { useTrialEndRefresh } from '@/hooks/useTrialEndRefresh';
import { FEATURE_FLAG_SPECS, TIER_FEATURE_GATES, type FeatureFlagKey } from '@aica/core';
import {
  EMBEDDED_SCREEN_FLAG_KEYS,
  featureGateVerdictSchema,
  proTrialViewSchema,
  type FeatureGateVerdict,
  type ProTrialView,
  type TierFeatureRefusal,
} from '@aica/api-types/schemas';
import { useOrganization } from './OrganizationContext';
import { ApiError } from '@/lib/fetch-utils';
import { logError } from '@/lib/logger';
import { retryTransientOnly } from '@/lib/query-retry';
import { TRIAL_START_REFRESH_MS } from '@/lib/pro-trial';

/**
 * Feature Flags Context - Single Responsibility: Feature flag management
 * Determines which features are enabled based on organization settings or global config
 */

export type FeatureFlags = Readonly<Record<FeatureFlagKey, boolean>>;

/**
 * Default feature flags — FAIL-CLOSED by design.
 *
 * All flags default to false. Features are enabled per-org when the
 * organization is created in the database. On auth/fetch failure the UI
 * shows a minimal interface until org-scoped flags arrive.
 *
 * See CP-005 / UI-005 in docs/plans/2026-03-16-configuration-precedence-audit.md.
 */
const DEFAULT_FLAGS: FeatureFlags = Object.freeze(
  Object.fromEntries(FEATURE_FLAG_SPECS.map((spec) => [spec.key, false])) as Record<
    FeatureFlagKey,
    boolean
  >
);

/**
 * A feature sold from a tier up, as this organisation stands with it (#1231):
 *
 * - `loading` — the answer has not arrived yet;
 * - `failed` — `/feature-flags` could not be read, or answered without a
 *   verdict (store degraded). Neither yes nor no: offer to ask again, never
 *   an upsell — an outage must not tell a Pro organisation to upgrade;
 * - `enabled`;
 * - `tier_not_eligible` — the plan is below the feature's floor (upgrade);
 * - `feature_disabled` — the plan includes it and the switch is off.
 */
export type FeatureGateState = 'loading' | 'failed' | 'enabled' | TierFeatureRefusal;

type FeatureGateVerdicts = Readonly<Record<string, FeatureGateVerdict>>;

/**
 * What the query holds. `gates` is `null` whenever there is no verdict to
 * give: the localStorage seed (verdicts are not cached — a stale "upgrade"
 * after an upgrade is the very lie this exists to remove) or a server older
 * than the field. A degraded answer is never held: it is a failure (below).
 */
interface FlagsSnapshot {
  flags: FeatureFlags;
  gates: FeatureGateVerdicts | null;
  /** The Pro trial to show (#2816); `null` on the seed, like `gates`. */
  proTrial: ProTrialView | null;
  /** `network` — the server's answer for this org; `cache` — the seed until it lands. */
  source: 'network' | 'cache';
}

interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  isEnabled: (flag: keyof FeatureFlags) => boolean;
  /** Where a tier-gated feature stands — see `FeatureGateState`. */
  gate: (flag: FeatureFlagKey) => FeatureGateState;
  /**
   * Ask `/feature-flags` again — offered when a gate is `failed`; settles when
   * the answer lands, with whether it was a good one. Nothing to ask counts as
   * good: there is nothing that failed.
   */
  retry: () => Promise<boolean>;
  /**
   * Whether `retry` asks anything. `false` where it does not (the Bitrix24
   * session carries the verdicts and changes with the session; no provider at
   * all): a screen must not offer the button. Required, so that a provider
   * written later says so instead of inheriting a button that does nothing.
   */
  canRefresh: boolean;
  /** The organisation the flag map is read for — a screen behind a gate asks again when it changes. */
  orgId: string | null;
  /**
   * The Pro trial of a Free organisation, as the server read it (#2816,
   * «Показ пробы»): `available` before it starts, `active` with `endsAt`,
   * `ended`. `null` — nothing to show: a paid organisation, the trial not open
   * to it, or no answer yet. The locks do not read it — `gate` already answers
   * on the trial's tier; it is for the banner and the lines on the lock.
   */
  proTrial: ProTrialView | null;
  isLoading: boolean;
  /** True once real flags are available (from network or localStorage cache). */
  isReady: boolean;
  /**
   * The server answered, or the read failed — the localStorage seed is no
   * longer all there is. What waits for `proTrial`, which the seed never
   * carries, waits for this, not for `isReady`.
   */
  answered: boolean;
  error: string | null;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

/** Flags sold from a tier up — their state comes from a verdict, never from the boolean. */
const TIER_GATED_FLAGS: ReadonlySet<string> = new Set(
  TIER_FEATURE_GATES.map((g) => g.flagKey)
);

const STORAGE_KEY = 'aica:feature-flags';

/**
 * How long one attempt to read `/feature-flags` may take, session token and body
 * included (#2182). Without a deadline a stalled connection holds the query
 * pending for as long as the browser keeps the socket, and every screen waiting
 * on it with it. Shorter than the entry wait below, so that a "retry" offered
 * when the wait runs out starts a new attempt instead of joining the dying one
 * (the one repeat the query layer makes after a network failure or a 503 is not
 * counted: it is the same read, still under way).
 */
const FEATURE_FLAGS_REQUEST_TIMEOUT_MS = 7_000;

/**
 * How long a screen that asks again on entry (`refreshOnMount`) waits for the
 * answer before it stops waiting and reads `failed` (#2182). The read goes on:
 * if it lands good, the screen leaves `failed` by itself.
 */
export const ENTRY_REFRESH_WAIT_MS = 8_000;

/** The query holding an organisation's flag map — one key for the provider and for whoever refreshes it. */
export const featureFlagsQueryKey = (orgId: string | null) =>
  ['feature-flags', orgId] as const;

function readCachedFlags(orgId: string | null): FlagsSnapshot | null {
  if (!orgId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${orgId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FeatureFlags>;
    return {
      flags: { ...DEFAULT_FLAGS, ...parsed },
      gates: null,
      proTrial: null,
      source: 'cache',
    };
  } catch {
    return null;
  }
}

function writeCachedFlags(orgId: string | null, flags: FeatureFlags): void {
  if (!orgId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(`${STORAGE_KEY}:${orgId}`, JSON.stringify(flags));
  } catch {
    // Quota or disabled storage — ignore silently.
  }
}

/** Each verdict checked on its own: one malformed entry must not void the rest. */
function parseGates(raw: unknown): FeatureGateVerdicts | null {
  if (!raw || typeof raw !== 'object') return null;
  const gates: Record<string, FeatureGateVerdict> = {};
  for (const [key, value] of Object.entries(raw)) {
    const parsed = featureGateVerdictSchema.safeParse(value);
    if (parsed.success) gates[key] = parsed.data;
  }
  return gates;
}

/**
 * The trial as received: a state only a newer edge-api knows, or a malformed
 * value, reads as nothing to show — never a failed flag map.
 */
function parseProTrial(raw: unknown): ProTrialView | null {
  const parsed = proTrialViewSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** A verdict in the gate's vocabulary — one translation for both providers. */
function verdictState(verdict: FeatureGateVerdict): FeatureGateState {
  return verdict.enabled ? 'enabled' : verdict.reason;
}

/**
 * Whether the flag map the query holds for `orgId` has the flag on — for code
 * that refreshed the map and must decide what to do with the answer, outside a
 * render. `null` wherever `gate()` would not give a verdict either: the map
 * holds no server answer for it (nothing cached, or only the localStorage
 * seed), the answer carries no verdicts, or a tier-gated flag's entry is missing.
 */
export function cachedFlagEnabled(
  queryClient: QueryClient,
  orgId: string | null,
  flag: FeatureFlagKey
): boolean | null {
  const snapshot = queryClient.getQueryData<FlagsSnapshot>(featureFlagsQueryKey(orgId));
  if (snapshot?.source !== 'network' || !snapshot.gates) return null;
  const verdict = snapshot.gates[flag];
  if (verdict) return verdictState(verdict) === 'enabled';
  return TIER_GATED_FLAGS.has(flag) ? null : Boolean(snapshot.flags[flag]);
}

/** `promise`, unless `signal` ends first — then its reason (a deadline's `TimeoutError`). */
function untilAborted<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(signal.reason);
    // Subscribed first: a rejection after the signal ended is still observed.
    promise
      .then(resolve, reject)
      .finally(() => signal.removeEventListener('abort', onAbort));
    if (signal.aborted) onAbort();
    else signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Fetch the org's flags. A failure THROWS: it used to resolve with the
 * defaults, which made "could not find out" indistinguishable from "off" and
 * showed a Pro organisation an upsell (#1231). The boolean map keeps its
 * fail-closed fallback in the provider; the gates report `failed`.
 *
 * Nothing to ask without a token or an organisation — answered here, before
 * any request: TanStack's `refetch()` runs the query function even when
 * `enabled` is false, and a request without an org header is answered for
 * some other org.
 *
 * A `degraded` answer (the store was unreadable, `flags` are platform defaults
 * and not this organisation's) is a failure of the same kind as a 503, and is
 * thrown as one: the previous answer, if there is one, stays in hand instead of
 * being replaced by defaults; nothing is written to the localStorage seed; and
 * it is asked once more, as a 503 is.
 */
async function fetchFeatureFlags(
  getToken: () => Promise<string | null>,
  orgId: string | null
): Promise<FlagsSnapshot> {
  // One deadline for the whole read, the session token included: a token that
  // never comes would otherwise leave the query pending, and a "retry" offered
  // for it would join that pending read instead of starting another.
  const signal = AbortSignal.timeout(FEATURE_FLAGS_REQUEST_TIMEOUT_MS);
  let data: {
    flags?: Partial<FeatureFlags>;
    gates?: unknown;
    degraded?: boolean;
    proTrial?: unknown;
  };
  try {
    const token = await untilAborted(getToken(), signal);
    if (!token) throw new ApiError('No session token for /feature-flags');
    if (!orgId) throw new ApiError('No organization selected for /feature-flags');

    const { API_BASE_URL } = await untilAborted(import('@/lib/api/config'), signal);
    // The deadline covers the body as well as the headers.
    const res = await fetch(`${API_BASE_URL}/api/v1/feature-flags`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Selected-Org-ID': orgId,
      },
      signal,
    });
    if (!res.ok) {
      throw new ApiError(`HTTP ${res.status}`, res.status, res.statusText);
    }
    data = (await res.json()) as typeof data;
  } catch (err) {
    // A deadline that ran out is a gateway-style failure with a status, not a
    // statusless one: the query layer asks again only after a network failure
    // or a 503, and a second full deadline would outlast the entry wait.
    if (signal.aborted) throw new ApiError('Timed out', 504, 'Gateway Timeout');
    throw err;
  }
  if (data.degraded) {
    throw new ApiError('Feature flag store degraded', 503, 'Service Unavailable');
  }
  return {
    // Merge with defaults to ensure all flags are present
    flags: { ...DEFAULT_FLAGS, ...data.flags },
    gates: parseGates(data.gates),
    proTrial: parseProTrial(data.proTrial),
    source: 'network',
  };
}

const flagsRetryDelay = (attemptIndex: number) =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  // Stable Clerk access — see useClerkGate.ts (token-refresh render storm).
  const { isLoaded: isClerkLoaded, isSignedIn } = useClerkGate();
  const getToken = useStableGetToken();
  const { selectedOrgId } = useOrganization();
  const queryClient = useQueryClient();

  const cachedFlags = useMemo(() => readCachedFlags(selectedOrgId), [selectedOrgId]);

  const queryFn = async (): Promise<FlagsSnapshot> => {
    try {
      const fresh = await fetchFeatureFlags(getToken, selectedOrgId);
      writeCachedFlags(selectedOrgId, fresh.flags);
      return fresh;
    } catch (err) {
      logError('Failed to load feature flags', {
        component: 'FeatureFlagsContext',
        orgId: selectedOrgId,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      throw err;
    }
  };

  const flagsQueryEnabled = isClerkLoaded && isSignedIn && !!selectedOrgId;

  // Fetch feature flags using TanStack Query
  const {
    data,
    isLoading,
    isError,
    error: queryError,
  } = useQuery<FlagsSnapshot, Error>({
    queryKey: featureFlagsQueryKey(selectedOrgId),
    queryFn,
    enabled: flagsQueryEnabled, // Fetch when org is selected
    // A network failure or a 503 is asked again once; a 4xx answers the same
    // however often it is asked (lib/query-retry).
    retry: retryTransientOnly,
    retryDelay: flagsRetryDelay,
    staleTime: 2 * 60 * 1000, // 2 minutes - allow quick updates from admin panel
    refetchOnWindowFocus: true,
    // A Pro trial waiting for its next analysis (#2816): the start is the
    // server's, so a visible tab asks again — a hidden one does not.
    refetchInterval: (query) =>
      query.state.data?.source === 'network' &&
      query.state.data.proTrial?.state === 'available'
        ? TRIAL_START_REFRESH_MS
        : false,
    initialData: cachedFlags ?? undefined,
    initialDataUpdatedAt: cachedFlags ? 0 : undefined,
  });

  // The server's answer, once there is one, survives a failed refetch: a
  // background blip must not close a section that is already open. Before
  // any answer, a failure falls back to the fail-closed defaults — not to the
  // localStorage seed, which only stands in while the request is in flight.
  const hasAnswer = data?.source === 'network';
  const flags = data && (hasAnswer || !isError) ? data.flags : DEFAULT_FLAGS;
  // A failure is an outcome too: screens waiting for flags stop waiting.
  const isReady = !!data || isError;

  // Helper to check if a feature is enabled
  const isEnabled = useMemo(() => {
    return (flag: keyof FeatureFlags) => Boolean(flags[flag]);
  }, [flags]);

  const gates = hasAnswer ? data.gates : null;
  const proTrial = hasAnswer ? data.proTrial : null;
  // Reads no fetch state: `isFetching` flips on every background refetch, and a
  // `gate` that followed it would change the context identity — and re-render
  // every consumer of the flag map — for a refetch that changed nothing. The
  // states that needed it do not: a retry after a failure with nothing in hand
  // resets the query to pending (no error, no answer → `loading`), and with an
  // answer in hand the answer is the verdict until a newer one replaces it.
  const gate = useCallback(
    (flag: FeatureFlagKey): FeatureGateState => {
      if (gates) {
        const verdict = gates[flag];
        if (verdict) return verdictState(verdict);
        // A tier-gated feature whose verdict is missing from an answer that
        // carries gates (a malformed entry dropped by the parser): not known.
        // Reading the boolean would tell a lower tier "switched off" instead
        // of "upgrade", or open a section the data route refuses.
        if (TIER_GATED_FLAGS.has(flag)) return 'failed';
        // Not a feature sold from a tier up: only the switch can close it.
        return flags[flag] ? 'enabled' : 'feature_disabled';
      }
      // An answer without verdicts (a server older than the field), or a
      // failure with nothing in hand.
      if (hasAnswer || isError) return 'failed';
      // Nothing yet: the first request, or a retry of it.
      return 'loading';
    },
    [gates, flags, hasAnswer, isError]
  );

  // Asked through the client by the organisation's key, not through the
  // observer's `refetch()` (which also ignores `enabled`): a screen that asks on an organisation switch runs
  // its effect before this provider's observer has moved to the new key, and
  // the observer's `refetch()` would read the OLD organisation again. Nothing
  // to ask, nothing to wait for, without an organisation.
  const retry = useCallback(async () => {
    if (!selectedOrgId) return true;
    // Failures are the query's own outcome (`error`, the gate's `failed`); the
    // caller waits for the answer to settle and is told whether it was good.
    return queryClient
      .fetchQuery({
        queryKey: featureFlagsQueryKey(selectedOrgId),
        queryFn,
        staleTime: 0,
        retry: retryTransientOnly,
        retryDelay: flagsRetryDelay,
      })
      .then(
        () => true,
        () => false
      );
    // `queryFn` is rebuilt every render and reads only `selectedOrgId` and the
    // stable token getter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, selectedOrgId]);

  useTrialEndRefresh(proTrial, retry);

  const error = queryError instanceof Error ? queryError.message : null;

  // Memoized so the context identity only changes when flags actually change —
  // an inline object here re-rendered all consumers on every provider render.
  const value = useMemo(
    () => ({
      flags,
      isEnabled,
      gate,
      retry,
      canRefresh: true,
      orgId: selectedOrgId,
      proTrial,
      isLoading,
      isReady,
      // Nothing to ask (no organisation, signed out): nothing to wait for.
      answered: hasAnswer || isError || !flagsQueryEnabled,
      error,
    }),
    [
      flags,
      isEnabled,
      gate,
      retry,
      selectedOrgId,
      proTrial,
      isLoading,
      isReady,
      hasAnswer,
      isError,
      flagsQueryEnabled,
      error,
    ]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
  );
}

/**
 * The flag map of a shell that has no `/feature-flags` to ask — the Bitrix24
 * iframe, where `FeatureFlagsProvider` cannot run (it is bound to Clerk). The
 * embedded session resolves the org's flags once and carries the allowlisted
 * subset (`embeddedSessionFlagsSchema`, #1347); this hands it to the same
 * context, so a screen reads `useFeatureFlagsSafe().isEnabled(key)` in both
 * shells instead of a per-feature verdict threaded through the session.
 *
 * - `flags` — `null` until the session is read; every flag outside the subset
 *   is off, exactly as with no provider at all;
 * - `gate` — a feature sold from a tier up answers from `gates` (#1897): the
 *   session's verdicts in the format `/feature-flags` sends, read by the same
 *   `parseGates` entry by entry. Where the session carries none (an edge-api
 *   older than the field, a read that failed, a session awaiting consent) or
 *   an entry cannot be read — a refusal code only a newer edge-api knows — the
 *   feature is `failed` ("could not find out"), never a guess. A flag outside
 *   the subset is `failed` too; a subset flag answers from its value;
 * - `screenFlagsOff` (#1859) — the flags of screens a card links to, which open
 *   in the portal and so are not in the subset: the session names the ones the
 *   org has off, and `gate` answers `feature_disabled` for them — what a link
 *   asks through `useFeatureKnownOff`. A flag not named stays `failed`
 *   (unknown), and so does a name outside `EMBEDDED_SCREEN_FLAG_KEYS`;
 * - `retry` — nothing to ask: the subset changes with the session;
 * - `proTrial` (#2816) — the session's trial, read by the same `parseProTrial`.
 */
export function SessionFeatureFlagsProvider({
  flags: sessionFlags,
  gates: sessionGates = null,
  screenFlagsOff = null,
  proTrial: sessionProTrial = null,
  orgId,
  error,
  children,
}: {
  flags: Readonly<Partial<Record<FeatureFlagKey, boolean>>> | null;
  /** The session's `gates`, as received: each entry is checked on its own. */
  gates?: unknown;
  /** The session's `screenFlagsOff`: names of the screen flags the org has off. */
  screenFlagsOff?: readonly string[] | null;
  /** The session's `proTrial`, as received. */
  proTrial?: unknown;
  orgId: string | null;
  error: string | null;
  children: ReactNode;
}) {
  const flags = useMemo<FeatureFlags>(
    () => (sessionFlags ? { ...DEFAULT_FLAGS, ...sessionFlags } : DEFAULT_FLAGS),
    [sessionFlags]
  );
  const verdicts = useMemo(() => parseGates(sessionGates), [sessionGates]);
  const proTrial = useMemo(() => parseProTrial(sessionProTrial), [sessionProTrial]);
  const screensOff = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        (screenFlagsOff ?? []).filter((name) =>
          (EMBEDDED_SCREEN_FLAG_KEYS as readonly string[]).includes(name)
        )
      ),
    [screenFlagsOff]
  );
  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      flags,
      isEnabled: (flag) => Boolean(flags[flag]),
      gate: (flag) => {
        if (TIER_GATED_FLAGS.has(flag)) {
          const verdict = verdicts?.[flag];
          return verdict ? verdictState(verdict) : 'failed';
        }
        if (screensOff.has(flag)) return 'feature_disabled';
        if (sessionFlags?.[flag] === undefined) return 'failed';
        return sessionFlags[flag] ? 'enabled' : 'feature_disabled';
      },
      retry: () => Promise.resolve(true),
      canRefresh: false,
      orgId,
      proTrial,
      isLoading: false,
      // A failed session is an outcome too: screens waiting for flags stop
      // waiting (and read every flag as off).
      isReady: sessionFlags !== null || error !== null,
      answered: sessionFlags !== null || error !== null,
      error,
    }),
    [flags, sessionFlags, verdicts, screensOff, orgId, proTrial, error]
  );
  return (
    <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
  );
}

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return context;
};

/**
 * Safe version of useFeatureFlags that returns defaults when provider is absent.
 * Use in leaf components (like ExportButton) that may render outside the provider in tests.
 */
const SAFE_FALLBACK: FeatureFlagsContextValue = {
  flags: DEFAULT_FLAGS,
  isEnabled: (flag: keyof FeatureFlags) => Boolean(DEFAULT_FLAGS[flag]),
  // No provider, no answer: "could not find out", not "no".
  gate: () => 'failed',
  retry: () => Promise.resolve(true),
  canRefresh: false,
  orgId: null,
  proTrial: null,
  isLoading: false,
  isReady: true,
  answered: true,
  error: null,
};

export const useFeatureFlagsSafe = () => {
  const context = useContext(FeatureFlagsContext);
  return context ?? SAFE_FALLBACK;
};

/** The Pro trial to show (#2816) — in both shells; `null` outside a provider. */
export const useProTrial = (): ProTrialView | null => useFeatureFlagsSafe().proTrial;

/**
 * Where a feature sold from a tier up stands for this organisation, and how to
 * ask again (#1231): "plan too low", "switched off" and "could not find out"
 * are three different screens. The server still decides — the feature's data
 * route refuses by the same rule.
 *
 * Outside the provider it answers `failed`. In the embedded Bitrix24
 * shell (`SessionFeatureFlagsProvider`) a feature sold from a tier up answers
 * from the verdict the session carries (#1897) — and `failed` where it carries
 * none (an older edge-api, a failed read, a session awaiting consent). There
 * `retry` asks nothing: the verdict changes with the session.
 */
export function useFeatureGate(
  flag: FeatureFlagKey,
  options: {
    refreshOnMount?: boolean;
    /**
     * The last read must have succeeded: an answer in hand from before a
     * failed read reads `failed`, not that answer. For a screen that builds
     * something to save from the verdict (the alert gallery, #2519) — an
     * obsolete verdict there is a wrong rule, not a section left open. Without
     * it the answer in hand survives a failed refetch, as it should for a
     * section already open.
     */
    requireFreshAnswer?: boolean;
  } = {}
): {
  state: FeatureGateState;
  retry: () => void;
  /** A `retry()` is in flight — `state` may stay `failed` until it lands. */
  retrying: boolean;
  /**
   * Whether `retry()` can ask anything: it needs an organisation and flags read
   * from `/feature-flags`. Outside the provider there is neither, and in the
   * Bitrix24 session the verdicts come with the session — there it is a no-op,
   * and a screen should not offer it.
   */
  canRetry: boolean;
  /**
   * The entry read (`refreshOnMount`) came back good. With `state` still
   * `failed` that is a verdict the answer does not carry — a server older than
   * the field, a dropped entry — and asking again cannot supply it (#2654).
   */
  readSucceeded: boolean;
} {
  const { gate, retry, canRefresh, orgId, error } = useFeatureFlagsSafe();
  const [retrying, setRetrying] = useState(false);
  // A plan can change outside this tab (hosted checkout, another admin), and
  // the map stays fresh for minutes: a screen behind the gate asks again on
  // entry and reads `loading` until that answer lands, never the old verdict.
  // The same holds when the organisation is switched on the open screen: the
  // new organisation's cached verdict is as old as its last read. The
  // organisation the answer was awaited for is recorded, not a bare flag — a
  // flag reset in an effect would show one render of the new organisation's
  // cached verdict before the effect ran. Without an organisation there is
  // nothing to ask and nothing to wait for.
  // What the entry read came to, for the organisation it was made for (#2182):
  // `wait` — the wait ran out with the read still pending; `read` — the read
  // came back failed; `null` — it came back good. Either failure reads `failed`
  // — an answer in hand from before is not shown as if it were this read's, a
  // plan changed outside the tab is the very thing it was asked for. `wait`
  // ends when the read does. `read` ends with the next good read of the query:
  // once the query's `error` has been seen and has cleared (a refocused tab
  // that recovers) — not at any later blip, which belongs to a section already
  // open; or with `retry`.
  const [entry, setEntry] = useState<{
    orgId: string;
    failed: 'wait' | 'read' | null;
    seenError: boolean;
  } | null>(null);
  const refreshing = options.refreshOnMount === true && !!orgId && entry?.orgId !== orgId;
  // A read settles after the screen may have moved to another organisation: its
  // outcome is then of no use to the new one.
  const orgNow = useRef(orgId);
  useEffect(() => {
    orgNow.current = orgId;
  });
  const record = (forOrg: string, ok: boolean) => {
    if (orgNow.current !== forOrg) return;
    setEntry({ orgId: forOrg, failed: ok ? null : 'read', seenError: false });
  };
  useEffect(() => {
    if (options.refreshOnMount !== true || !orgId) return;
    const timer = setTimeout(
      () => setEntry({ orgId, failed: 'wait', seenError: false }),
      ENTRY_REFRESH_WAIT_MS
    );
    void retry().then((ok) => {
      clearTimeout(timer);
      record(orgId, ok);
    });
    return () => {
      clearTimeout(timer);
      setEntry((prev) => (prev?.orgId === orgId ? null : prev));
    };
    // `retry` follows the organisation; this runs once per mount and per
    // organisation switch, not per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);
  useEffect(() => {
    if (entry?.failed !== 'read') return;
    if (error !== null && !entry.seenError) setEntry({ ...entry, seenError: true });
    else if (error === null && entry.seenError) {
      setEntry({ orgId: entry.orgId, failed: null, seenError: false });
    }
  }, [entry, error]);
  // Before the query's error is seen, `read` already holds: the outcome of
  // `retry()` and the observer's `error` reach React separately.
  const entryFailed =
    options.refreshOnMount === true &&
    entry?.orgId === orgId &&
    (entry.failed === 'wait' ||
      (entry.failed === 'read' && (error !== null || !entry.seenError)));
  const stale = options.requireFreshAnswer === true && error !== null;
  return {
    state: refreshing ? 'loading' : entryFailed || stale ? 'failed' : gate(flag),
    retry: () => {
      setRetrying(true);
      void retry()
        .then((ok) => {
          if (options.refreshOnMount === true && orgId) record(orgId, ok);
        })
        .finally(() => setRetrying(false));
    },
    retrying,
    canRetry: !!orgId && canRefresh,
    readSucceeded:
      options.refreshOnMount === true && entry?.orgId === orgId && entry.failed === null,
  };
}
